import { MutableRefObject, useEffect } from 'react';
import {
  getAgentCacheEntry,
  getSyncCacheEntry,
  isCacheValid,
  markSolutionSynced,
  setAgentCacheEntry,
} from '../solutionCache';
import {
  checkIfSyncNeeded,
  hasCachedEnablementData,
  mergeAgentSyncData,
  mergeSyncResponses,
  validateAgentDataCompleteness,
} from '../solutionUtils';
import { getEnterpriseTechnology } from '../../../../services/commonService';
import { ENVIRONMENT_TYPE } from '../../../../config/env';
import { makeAiEnablementSyncCall, makeDataSourcesSyncCall } from '../solutionApiService';

interface UseEnterpriseTechSyncOptions {
  aiSolutionData: any;
  selectedSolutionId: string | number | null;
  currentOrg: string;
  diagramId?: string;
  processType?: string;
  /** Ref that is set to `true` while a save is in flight — prevents sync from stomping save data */
  hasFetchedEnterpriseTechRef: MutableRefObject<boolean>;
  missingAgentDataDecisionCountRef: MutableRefObject<Record<string, number>>;
  // Setters
  setAgentLlmResponse: (data: any[]) => void;
  setLlmResponseData: (data: any[]) => void;
  setUpdatedSolutionData: (fn: (prev: any) => any) => void;
  setAiSolutionData?: (data: any) => void;
  setHasEnterpriseTechApplied: (value: boolean) => void;
  setLlmResponseLoading: (value: boolean) => void;
  setIsLoadingAgentLlmResponse: (value: boolean) => void;
  setIsLoadingEnterpriseTech: (value: boolean) => void;
}

/**
 * Runs the enterprise-tech sync whenever `aiSolutionData.id`,
 * `aiSolutionData.agent.updated_at`, or `selectedSolutionId` changes.
 */
export const useEnterpriseTechSync = ({
  aiSolutionData,
  selectedSolutionId,
  currentOrg,
  diagramId,
  processType,
  hasFetchedEnterpriseTechRef,
  missingAgentDataDecisionCountRef,
  setAgentLlmResponse,
  setLlmResponseData,
  setUpdatedSolutionData,
  setAiSolutionData,
  setHasEnterpriseTechApplied,
  setLlmResponseLoading,
  setIsLoadingAgentLlmResponse,
  setIsLoadingEnterpriseTech,
}: UseEnterpriseTechSyncOptions) => {
  useEffect(() => {
    if (!aiSolutionData?.id || !currentOrg || !selectedSolutionId) return;

    // Guard: ensure the data in state matches the currently-selected solution
    if (String(aiSolutionData.id) !== String(selectedSolutionId)) {
      console.log(
        `⚠️ Solution ID mismatch: aiSolutionData.id (${aiSolutionData.id}) !== selectedSolutionId (${selectedSolutionId}). Waiting for sync...`
      );
      return;
    }

    const solutionId = String(selectedSolutionId);

    const run = async () => {
      if (!aiSolutionData?.id) return;

      // Re-validate ID match after async gap
      if (String(aiSolutionData.id) !== String(selectedSolutionId)) {
        console.log(`⚠️ Solution ID mismatch during fetch. Aborting.`);
        hasFetchedEnterpriseTechRef.current = false;
        return;
      }

      // Prevent duplicate execution within the same render cycle
      if (hasFetchedEnterpriseTechRef.current) return;
      hasFetchedEnterpriseTechRef.current = true;

      // ── Cache hit path ────────────────────────────────────────────────────
      const syncEntry = getSyncCacheEntry(solutionId);
      const cachedAgentData = getAgentCacheEntry(solutionId);

      if (syncEntry && !syncEntry.wasManualSync && isCacheValid(solutionId) && cachedAgentData) {
        const cachedSolutionId = cachedAgentData.solutionData?.id;

        if (cachedSolutionId && String(cachedSolutionId) !== solutionId) {
          console.warn(`⚠️ Cached data ID mismatch. Skipping cache.`);
          hasFetchedEnterpriseTechRef.current = false;
        } else if (!validateAgentDataCompleteness(cachedAgentData.solutionData)) {
          console.warn(`⚠️ Cached data incomplete. Skipping cache.`);
          hasFetchedEnterpriseTechRef.current = false;
        } else if (cachedAgentData.agentUpdatedAt !== (aiSolutionData?.agent?.updated_at ?? null)) {
          hasFetchedEnterpriseTechRef.current = false;
        } else {
          console.log('✅ Using cached agent data — skipping sync calls');
          try {
            const enterpriseTechResponse = await getEnterpriseTechnology(currentOrg);
            const hasEnterpriseTech =
              !!enterpriseTechResponse?.industry_data &&
              Object.keys(enterpriseTechResponse.industry_data).length > 0;

            if (cachedAgentData.agentData) {
              setAgentLlmResponse(cachedAgentData.agentData.agentLlmResponse || []);
              setLlmResponseData(cachedAgentData.agentData.llmResponseData || []);
            }
            if (cachedAgentData.solutionData) {
              setUpdatedSolutionData(() => cachedAgentData.solutionData);
              setAiSolutionData?.(cachedAgentData.solutionData);
              const hasEnablement = hasCachedEnablementData(cachedAgentData.solutionData);
              setHasEnterpriseTechApplied(hasEnterpriseTech && hasEnablement);
            }
          } catch {
            // On error, still restore cached data
            if (cachedAgentData.agentData) {
              setAgentLlmResponse(cachedAgentData.agentData.agentLlmResponse || []);
              setLlmResponseData(cachedAgentData.agentData.llmResponseData || []);
            }
            if (cachedAgentData.solutionData) {
              setUpdatedSolutionData(() => cachedAgentData.solutionData);
              setAiSolutionData?.(cachedAgentData.solutionData);
              if (hasCachedEnablementData(cachedAgentData.solutionData))
                setHasEnterpriseTechApplied(true);
            }
          } finally {
            setIsLoadingEnterpriseTech(false);
            setLlmResponseLoading(false);
            setIsLoadingAgentLlmResponse(false);
          }
          return;
        }
      }

      // ── Fresh fetch path ──────────────────────────────────────────────────
      try {
        setIsLoadingEnterpriseTech(true);
        setLlmResponseLoading(true);

        const enterpriseTechResponse = await getEnterpriseTechnology(currentOrg);
        const hasEnterpriseTech =
          !!enterpriseTechResponse?.industry_data &&
          Object.keys(enterpriseTechResponse.industry_data).length > 0;

        setHasEnterpriseTechApplied(hasEnterpriseTech);
        if (!hasEnterpriseTech) return;

        // Guard against empty agent key arriving after initial render
        const hasExistingAgentData =
          aiSolutionData.agent &&
          (aiSolutionData.agent.ai_enablement ||
            aiSolutionData.agent.agent_classification ||
            aiSolutionData.agent.embedded ||
            aiSolutionData.agent.extended ||
            aiSolutionData.agent.custom);

        if (!hasExistingAgentData) {
          const count = (missingAgentDataDecisionCountRef.current[solutionId] ?? 0) + 1;
          missingAgentDataDecisionCountRef.current[solutionId] = count;
          const hasAgentKey = Object.prototype.hasOwnProperty.call(aiSolutionData, 'agent');
          if (count === 1 && !hasAgentKey && aiSolutionData?.agent_available == null) return;
        }

        const additionalPayload = {
          llm_agent: aiSolutionData.llm_agent,
          solution_enabler: aiSolutionData.solution_enabler,
          diagram_id: diagramId,
          organization_id: currentOrg,
          type: ENVIRONMENT_TYPE,
        };

        const runFullSync = async () => {
          const dataSourcesResponse = await makeDataSourcesSyncCall(
            aiSolutionData,
            enterpriseTechResponse,
            additionalPayload,
            processType
          );

          const firstResponseData =
            dataSourcesResponse?.application_in_use || dataSourcesResponse?.ai_solution || [];
          setLlmResponseData(firstResponseData);
          setUpdatedSolutionData((prev: any) => ({
            ...prev,
            agent: { ...(prev?.agent || {}), ai_solution: firstResponseData },
          }));
          setLlmResponseLoading(false);

          const enablementResponse = await makeAiEnablementSyncCall(
            aiSolutionData,
            enterpriseTechResponse,
            additionalPayload,
            processType
          );

          const combined = mergeSyncResponses(dataSourcesResponse, enablementResponse);
          const agentResponseData =
            combined?.agent_classification ||
            combined?.ai_enablement?.enablement_matrix?.embedded ||
            combined?.embedded ||
            [];

          setAgentLlmResponse(agentResponseData);
          setLlmResponseData(combined?.application_in_use || combined?.ai_solution || []);

          setUpdatedSolutionData((prev: any) => {
            const merged = mergeAgentSyncData(prev || aiSolutionData, combined);
            setAiSolutionData?.(merged);

            const hasComplete =
              merged?.agent?.ai_enablement &&
              (merged?.agent?.ai_solution || merged?.agent?.application_in_use);
            if (hasComplete) {
              setAgentCacheEntry(solutionId, {
                solutionData: merged,
                agentData: {
                  agentLlmResponse: agentResponseData,
                  llmResponseData: combined?.application_in_use || combined?.ai_solution || [],
                },
                agentUpdatedAt: merged?.agent?.updated_at ?? null,
              });
              markSolutionSynced(solutionId, false, true);
            }
            return merged;
          });
        };

        if (hasExistingAgentData) {
          if (aiSolutionData.agent.updated_at) {
            const shouldSync = await checkIfSyncNeeded(
              enterpriseTechResponse,
              aiSolutionData.agent
            );
            if (shouldSync) {
              setIsLoadingAgentLlmResponse(true);
              try {
                await runFullSync();
              } catch (error) {
                console.error('Sync failed:', error);
                // Fall back to existing agent data
                setAgentLlmResponse(
                  aiSolutionData.agent.agent_classification ||
                    aiSolutionData.agent.embedded ||
                    aiSolutionData.agent.extended ||
                    aiSolutionData.agent.custom ||
                    aiSolutionData.agent.ai_enablement ||
                    []
                );
                setLlmResponseData(
                  aiSolutionData.agent?.ai_solution ||
                    aiSolutionData.agent?.application_in_use ||
                    []
                );
                setLlmResponseLoading(false);
              } finally {
                setIsLoadingAgentLlmResponse(false);
              }
            } else {
              // Agent is up to date
              setAgentLlmResponse(
                aiSolutionData.agent.agent_classification ||
                  aiSolutionData.agent.embedded ||
                  aiSolutionData.agent.extended ||
                  aiSolutionData.agent.custom ||
                  aiSolutionData.agent.ai_enablement ||
                  []
              );
              setLlmResponseData(
                aiSolutionData.agent?.ai_solution || aiSolutionData.agent?.application_in_use || []
              );
              setIsLoadingAgentLlmResponse(false);

              const hasComplete =
                aiSolutionData.agent?.ai_enablement &&
                (aiSolutionData.agent?.ai_solution || aiSolutionData.agent?.application_in_use);
              if (hasComplete) {
                setAgentCacheEntry(solutionId, {
                  solutionData: aiSolutionData,
                  agentData: {
                    agentLlmResponse:
                      aiSolutionData.agent.agent_classification ||
                      aiSolutionData.agent.embedded ||
                      aiSolutionData.agent.extended ||
                      aiSolutionData.agent.custom ||
                      aiSolutionData.agent.ai_enablement ||
                      [],
                    llmResponseData:
                      aiSolutionData.agent?.ai_solution ||
                      aiSolutionData.agent?.application_in_use ||
                      [],
                  },
                });
                markSolutionSynced(solutionId, false, true);
              }
            }
          } else {
            // Agent exists but has no timestamp
            const hasEnablementMatrix = !!aiSolutionData.agent?.ai_enablement?.enablement_matrix;
            if (!hasEnablementMatrix) {
              setIsLoadingAgentLlmResponse(true);
              try {
                await runFullSync();
              } catch (err) {
                console.error('Failed to sync timestamp-less agent:', err);
              } finally {
                setIsLoadingAgentLlmResponse(false);
                setLlmResponseLoading(false);
              }
            } else {
              setAgentLlmResponse(
                aiSolutionData.agent.agent_classification ||
                  aiSolutionData.agent.embedded ||
                  aiSolutionData.agent.extended ||
                  aiSolutionData.agent.custom ||
                  aiSolutionData.agent.ai_enablement ||
                  []
              );
              setLlmResponseData(
                aiSolutionData.agent.ai_solution || aiSolutionData.agent.application_in_use || []
              );
            }
          }
        } else {
          // No agent data at all — generate via sync
          const persistedTs = sessionStorage.getItem(
            `simulation_sync_agent_updated_at_${solutionId}`
          );
          if (persistedTs) {
            const shouldSync = await checkIfSyncNeeded(enterpriseTechResponse, {
              updated_at: persistedTs,
            });
            if (!shouldSync) {
              setIsLoadingAgentLlmResponse(false);
              setLlmResponseLoading(false);
              return;
            }
          }

          setIsLoadingAgentLlmResponse(true);
          try {
            await runFullSync();
          } catch (err) {
            console.error('Failed to generate agent data:', err);
            setLlmResponseLoading(false);
            markSolutionSynced(solutionId, false, false);
          } finally {
            setIsLoadingAgentLlmResponse(false);
          }
        }
      } catch (error) {
        console.error('Enterprise tech sync failed:', error);
      } finally {
        setIsLoadingEnterpriseTech(false);
        setLlmResponseLoading(false);
        setIsLoadingAgentLlmResponse(false);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    aiSolutionData?.id,
    aiSolutionData?.agent?.updated_at,
    selectedSolutionId,
    currentOrg,
    diagramId,
  ]);
};
